import type { ActionTemplateParameterResource } from "./actionTemplateParameterResource";
import type { ICommitCommand } from "./commitCommand";
import type { IProcessResource, ModifyProcessCommand } from "./deploymentProcessResource";
import type { IconResource } from "./iconResource";
import type { IId } from "./resource";
import type { TemplateParameterResource } from "./templateParameterResource";
import type { TemplateResourceBase } from "./templateResource";

export interface ProcessTemplateResource extends TemplateResourceBase, IProcessResource, IId {
    Parameters: ProcessTemplateParameterResource[];
}

export interface ProcessTemplateListSummaryResource {
    Name: string;
    Slug: string;
    Description?: string;
    Icon?: IconResource;
    CanSpecifyVersion: boolean;
    IsPreReleaseOnly: boolean;
}

export type ModifyProcessTemplateCommand = ProcessTemplateResource & ICommitCommand;

export function isModifyProcessTemplateCommand(command: ModifyProcessCommand): command is ModifyProcessTemplateCommand {
    return "Parameters" in command;
}

export interface DeleteProcessTemplateCommand extends ICommitCommand {
    ProcessTemplateSlug: string;
    GitRef: string;
}

export interface NewProcessTemplate extends ICommitCommand {
    Name: string;
    Description: string;
}

export interface CreateProcessTemplateVersionCommand {
    ProcessTemplateSlug: string;
    GitRef: string;
    Version: string;
    IsPreRelease: boolean;
}

export interface ProcessTemplateSummary extends IId {
    Name: string;
    ProjectId: string;
    GitRef: string;
    Slug: string;
    Description?: string;
    Icon?: IconResource;
    Version?: string;
    PublishedDate?: string;
    HasError?: boolean;
}

export interface ProcessTemplateVersionResource {
    ProcessTemplateSlug: string;
    GitCommit: string;
    GitRef: string;
    Version: string;
    PublishedDate: string;
    IsPreRelease: boolean;
}

export interface ProcessTemplateVersionSummary {
    ProcessTemplateSlug: string;
    LatestStableVersion: string | null;
    LatestPreReleaseVersion: string | null;
    IsOnlyPreRelease: boolean;
}

export interface GetProcessTemplateSharingConfigurationResponse {
    SharedToAllSpaces: boolean;
    IndividuallySharedSpaceIds: string[];
}

export interface ShareProcessTemplateCommand {
    ProcessTemplateSlug: string;
    GitRef: string;
    ShareToAllSpaces: boolean;
    IndividuallySharedSpaceIds: string[];
}

export interface ShareProcessTemplateResponse {
    SharedToAllSpaces: boolean;
    IndividuallySharedSpaceIds: string[];
    IndividuallyUnsharedSpaceIds: string[];
}

export interface ProcessTemplateParameterResource extends TemplateParameterResource {}

export const isProcessTemplateParameterResource = (resource: ActionTemplateParameterResource): resource is ProcessTemplateParameterResource => {
    return "Values" in resource;
};

export const unbindParameterName = (bindedParameterName: string | null | undefined): string | undefined => {
    const bindingPattern = /^#{(.+)}$/;
    const match = bindedParameterName ? bindedParameterName.match(bindingPattern) : undefined;
    return match ? match[1] : (bindedParameterName ?? undefined);
};

export const bindParameterName = (parameterName: string) => `#{${parameterName}}`;
